import React from 'react';
import _filter from 'lodash/filter';
import _uniq from 'lodash/uniq';
import { Button, KIND, SIZE } from 'baseui/button';
import { ERASED_KEY_STATUSES, KEY_ISSUE_STATUSES, KEY_STATUS, MESSAGES, STALE_DATA_DAYS } from '../constants';
import { DpdpaSource, ExportFormat, UserData } from '../types';
import useStyles from '../useStyles';
import { formatDate, isStale } from '../utils/format';
import { KeyStatusBadge } from './common/Badge';
import Notice from './common/Notice';
import ExportActions from './ExportActions';
import SourceSection from './SourceSection';

interface IDataViewProps {
	data: UserData;
	canExport: boolean;
	isLoading: boolean;
	onExport: (format: ExportFormat) => void;
	onBack: () => void;
}

const ErasedPanel: React.FC<{ data: UserData }> = ({ data }) => {
	const by: string = data.erasure?.by || (data.keyStatus === KEY_STATUS.ERASED_BY_LEGAL ? 'legal' : 'user');
	const actor: string = by === 'legal' ? 'by legal' : 'by the user';
	const date: string = data.erasure?.date ? ` on ${formatDate(data.erasure.date)}` : '';
	return (
		<Notice tone='neutral' title='Data erased.'>
			{`Personal data was erased ${actor}${date}. Encrypted fields are no longer recoverable. Non-encrypted fields still held are shown below.`}
		</Notice>
	);
};

const DataView: React.FC<IDataViewProps> = ({ data, canExport, isLoading, onExport, onBack }) => {
	const { classes } = useStyles();
	const isErased: boolean = ERASED_KEY_STATUSES.includes(data.keyStatus);
	const hasKeyIssue: boolean = KEY_ISSUE_STATUSES.includes(data.keyStatus);
	const staleSources: DpdpaSource[] = _filter(data.sources, (source) => source.rowFound && isStale(source.dataAsOf));
	// de-duplicated: repeats add nothing, and each warning's text is its list key
	const warnings: string[] = _uniq(data.warnings || []);

	return (
		<section aria-label='Review and export' className={classes.stack}>
			<div className={classes.dataHeader}>
				<div className={classes.headerMeta}>
					<div className={classes.contextItem}>
						<span className={classes.contextLabel}>UID</span>
						<span className={classes.mono}>{data.uid}</span>
					</div>
					<div className={classes.contextItem}>
						<span className={classes.contextLabel}>Fetched at</span>
						<span className={classes.mono}>{formatDate(data.fetchedAt)}</span>
					</div>
					<div className={classes.contextItem}>
						<span className={classes.contextLabel}>Key status</span>
						<span>
							<KeyStatusBadge keyStatus={data.keyStatus} />
						</span>
					</div>
					<div className={classes.contextItem}>
						<span className={classes.contextLabel}>Sources</span>
						{data.sources.map((source) => (
							<span key={source.table}>
								{source.label || source.table}
								{source.dataAsOf && (
									<span className={classes.muted}>{` · Data as of ${formatDate(source.dataAsOf)}`}</span>
								)}
							</span>
						))}
					</div>
				</div>
				<div className={classes.actionsRow}>
					<ExportActions canExport={canExport} isLoading={isLoading} onExport={onExport} />
					<Button kind={KIND.tertiary} size={SIZE.compact} disabled={isLoading} onClick={onBack}>
						Back to accounts
					</Button>
				</div>
			</div>

			{isErased && <ErasedPanel data={data} />}
			{hasKeyIssue && <Notice tone='danger'>{MESSAGES.KEY_ISSUE}</Notice>}
			{/* for an erased user the missing fields are explained by the erasure panel above */}
			{data.partial && !isErased && <Notice tone='warning'>{MESSAGES.PARTIAL}</Notice>}
			{staleSources.length > 0 && (
				<Notice tone='warning' title='Possibly stale data.'>
					{`${staleSources.map((source) => source.label || source.table).join(', ')} ${
						staleSources.length > 1 ? 'are' : 'is'
					} more than ${STALE_DATA_DAYS} days old; recent changes may not be reflected.`}
				</Notice>
			)}
			{warnings.length > 0 && (
				<Notice tone='warning' title='Warnings.'>
					{warnings.length === 1 ? (
						warnings[0]
					) : (
						<ul className={classes.noticeList}>
							{warnings.map((warning) => (
								<li key={warning}>{warning}</li>
							))}
						</ul>
					)}
				</Notice>
			)}

			{data.sources.map((source) => (
				<SourceSection key={source.table} source={source} hideEncrypted={isErased} canCopy={canExport} />
			))}
		</section>
	);
};

export default DataView;
